import clsx from 'clsx'
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactElement,
} from 'react'

import { withPublicVideoUrl } from '@lib/utils/cdn'

import style from './hero-background-video.module.css'

interface HeroBackgroundVideoRef {
  play: () => void
  pause: () => void
}

interface HeroBackgroundVideoProps extends ComponentPropsWithRef<'video'> {
  /* Optional poster image to show while video is loading */
  poster?: string
  /* Optional delay before starting to load the video */
  loadingDelay?: number
}

export function HeroBackgroundVideo(props: HeroBackgroundVideoProps): ReactElement {
  const { className, poster, loadingDelay = 100, ref, ...rest } = props
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const shouldPlayRef = useRef<boolean>(true) // Track if video should be playing based on visibility

  // Expose play/pause methods to parent
  useImperativeHandle<HeroBackgroundVideoRef, HeroBackgroundVideoRef>(ref, () => ({
    play: () => {
      shouldPlayRef.current = true
      if (!document.hidden) {
        videoRef.current?.play().catch(() => {
          // Autoplay was prevented, which is fine
        })
      }
    },
    pause: () => {
      shouldPlayRef.current = false
      videoRef.current?.pause()
    },
  }))

  // Lazy load video to improve initial page load time
  useEffect(() => {
    const startVideoLoading = () => setShouldLoadVideo(true)
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let idleCallbackId: number | undefined

    // Use requestIdleCallback if available, otherwise fallback to setTimeout
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackId = requestIdleCallback(
        () => {
          timeoutId = setTimeout(startVideoLoading, loadingDelay)
        },
        { timeout: 5000 },
      ) // 5s timeout fallback
    } else {
      timeoutId = setTimeout(startVideoLoading, loadingDelay)
    }

    // Cleanup function to cancel pending timers
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (idleCallbackId && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        cancelIdleCallback(idleCallbackId)
      }
    }
  }, [loadingDelay])

  // Handle visibility change to pause/play the video based on the visibility of the tab
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - pause the video
        video.pause()
      } else if (shouldPlayRef.current) {
        // Tab is visible AND video should be playing (hero is in view)
        // i.e. only play if it should be playing (not paused by intersection observer)
        video.play().catch(() => {
          // Autoplay was prevented, which is fine
        })
      }
      // If tab is visible but shouldPlayRef.current is false, do nothing (hero is out of view)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [shouldLoadVideo])

  return (
    <div className={clsx(style.videoContainer, className)}>
      {shouldLoadVideo && (
        // Intentionally not using the Video component to avoid the intersection observer. This background video is fixed to
        // the viewport and its play/paused state is controlled by the visibilitychange event listener and the Hero component.
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          poster={poster}
          aria-hidden="true"
          className={clsx(style.video)}
          {...rest}
        >
          <source src={withPublicVideoUrl('shared/hero-background.mp4')} type="video/mp4" />
          Sorry, your browser doesn't support embedded videos.
        </video>
      )}
    </div>
  )
}
